import { useCallback, useRef, useSyncExternalStore, type RefObject } from "react";

import { getElementOverlayGeometry, type OverlayGeometry } from "@skilitics/drag-drop";

import { DND_CANVAS_BACKGROUND_ID, DND_CANVAS_EDITOR_ID } from "../dnd/index.ts";

const initialGeometry: OverlayGeometry = {
  boundingRect: { top: 0, left: 0, width: 0, height: 0 },
  intrinsicRect: { top: 0, left: 0, width: 0, height: 0 },
  transform: "none",
  transformOrigin: "0 0",
  overlayStyle: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    transform: "none",
    transformOrigin: "0 0",
  },
  scaleX: 1,
  scaleY: 1,
  borderRadius: { rx: 0, ry: 0 },
  rotation: 0,
};

const getEditorRelativeGeometry = (element: HTMLElement): OverlayGeometry =>
  getElementOverlayGeometry(element, document.getElementById(DND_CANVAS_EDITOR_ID));

const isRectEqual = (a: OverlayGeometry["boundingRect"], b: OverlayGeometry["boundingRect"]) =>
  a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

const isGeometryEqual = (a: OverlayGeometry, b: OverlayGeometry) =>
  isRectEqual(a.boundingRect, b.boundingRect) &&
  isRectEqual(a.intrinsicRect, b.intrinsicRect) &&
  a.transform === b.transform &&
  a.scaleX === b.scaleX &&
  a.scaleY === b.scaleY &&
  a.borderRadius.rx === b.borderRadius.rx &&
  a.borderRadius.ry === b.borderRadius.ry &&
  a.rotation === b.rotation;

// Single shared MutationObserver on the canvas background. Each active wrapper
// registers a callback; one DOM observer fans out to all of them. Avoids
// attaching N observers when N wrappers are visible.
//
// The canvas background may not be in the DOM at first subscribe (wrappers can
// mount before the canvas mounts). We retry with rAF until the target is
// available — listeners that arrive in the gap are still tracked and start
// firing as soon as the target appears.
type CanvasTransformListener = () => void;
const canvasTransformListeners = new Set<CanvasTransformListener>();
let canvasTransformObserver: MutationObserver | null = null;
let canvasTransformAttachHandle: number | null = null;

const attachCanvasTransformObserver = () => {
  if (canvasTransformObserver) return true;
  const target = document.getElementById(DND_CANVAS_BACKGROUND_ID);
  if (!target) return false;
  canvasTransformObserver = new MutationObserver(() => {
    for (const listener of canvasTransformListeners) listener();
  });
  canvasTransformObserver.observe(target, { attributes: true, attributeFilter: ["style"] });
  return true;
};

const ensureCanvasTransformObserver = () => {
  if (attachCanvasTransformObserver()) return;
  if (canvasTransformAttachHandle !== null) return;
  const tryAttach = () => {
    canvasTransformAttachHandle = null;
    if (canvasTransformListeners.size === 0) return;
    if (attachCanvasTransformObserver()) return;
    canvasTransformAttachHandle = requestAnimationFrame(tryAttach);
  };
  canvasTransformAttachHandle = requestAnimationFrame(tryAttach);
};

const subscribeToCanvasTransform = (listener: CanvasTransformListener): (() => void) => {
  canvasTransformListeners.add(listener);
  ensureCanvasTransformObserver();
  return () => {
    canvasTransformListeners.delete(listener);
    if (canvasTransformListeners.size === 0) {
      if (canvasTransformObserver) {
        canvasTransformObserver.disconnect();
        canvasTransformObserver = null;
      }
      if (canvasTransformAttachHandle !== null) {
        cancelAnimationFrame(canvasTransformAttachHandle);
        canvasTransformAttachHandle = null;
      }
    }
  };
};

const subscribeToElementGeometry = (element: HTMLElement, onStoreChange: () => void) => {
  // Observe size changes (also captures some position changes from reflow)
  const resizeObserver = new ResizeObserver(onStoreChange);
  resizeObserver.observe(element, { box: "border-box" });

  // Observe CSS class changes on the element (Emotion style updates change className)
  const classObserver = new MutationObserver(onStoreChange);
  classObserver.observe(element, { attributes: true, attributeFilter: ["style", "class"] });

  // Share a single canvas-zoom/pan observer across all active wrappers.
  const unsubscribeCanvas = subscribeToCanvasTransform(onStoreChange);

  return () => {
    resizeObserver.disconnect();
    classObserver.disconnect();
    unsubscribeCanvas();
  };
};

/**
 * Tracks the overlay geometry needed to align borders, resize handles, and
 * resize previews with a transformed canvas element. Purely observer-driven.
 *
 * `preventUpdate` gates BOTH subscription and snapshot — when true, the wrapper
 * attaches no observers and returns identity geometry. The wrapper is expected
 * to pass `true` when it isn't currently rendering any overlay (not selected,
 * not hovered, not in layout mode, not resizing). This is what keeps the cost
 * flat at thousands of wrappers: idle wrappers cost nothing.
 *
 * Active wrappers update when:
 * - Element resizes (ResizeObserver)
 * - Element's style/class changes (MutationObserver)
 * - Canvas zoom/pan changes (one shared MutationObserver, fanned out)
 */
export const useCanvasElementOverlayGeometry = (ref: RefObject<HTMLElement | null>, preventUpdate?: boolean) => {
  const cachedGeometry = useRef(initialGeometry);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!ref.current || preventUpdate) return () => {};
      return subscribeToElementGeometry(ref.current, onStoreChange);
    },
    [ref, preventUpdate],
  );

  const getSnapshot = useCallback((): OverlayGeometry => {
    if (!ref.current || preventUpdate) return initialGeometry;

    const next = getEditorRelativeGeometry(ref.current);
    if (!isGeometryEqual(cachedGeometry.current, next)) {
      cachedGeometry.current = next;
    }
    return cachedGeometry.current;
  }, [ref, preventUpdate]);

  return useSyncExternalStore(subscribe, getSnapshot);
};
